export type OfferMatchType = "exact" | "variant" | "fuzzy";

export interface OfferIdentityInput {
  requestedMpn: string;
  requestedManufacturer?: string | null;
  candidateMpn?: string | null;
  candidateManufacturer?: string | null;
  candidateTitle?: string | null;
}

export interface OfferIdentityMatch {
  matchType: OfferMatchType;
  requestedMpn: string;
  candidateMpn: string;
  normalizedRequestedMpn: string;
  normalizedCandidateMpn: string;
  normalizedRequestedManufacturer: string;
  normalizedCandidateManufacturer: string;
  manufacturerMatch: boolean | null;
  explanation: string;
}

/**
 * Canonical MPN normalization. Deliberately conservative: case and punctuation
 * are ignored, but visually-similar characters are never substituted.
 */
export function normalizeMpn(value: string | null | undefined): string {
  return (value ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * Infers only manufacturer families with highly distinctive catalogue-number
 * prefixes. This is used to find an official OEM record; it never changes a
 * supplier's reported manufacturer or promotes an offer to an exact match.
 */
export function inferManufacturerFromMpn(value: string | null | undefined): string | null {
  const mpn = normalizeMpn(value);
  if (/^(?:6ES|6AV|6GK|6EP|6SL|6FX|6BK)\d/.test(mpn)) return "Siemens";
  if (/^(?:1756|1769|1768|1746|1734|1771|1783|1794|2711)[A-Z0-9]/.test(mpn)) return "Allen-Bradley";
  if (/^(?:BMX|BME|BMH|TSX|TM[2345]|HMIG|ATV(?:12|212|312|320|340|600|900))[A-Z0-9]/.test(mpn)) return "Schneider Electric";
  if (/^MTL\d/.test(mpn)) return "Eaton";
  return null;
}

const MANUFACTURER_IDENTITIES: Array<{ canonical: string; aliases: string[] }> = [
  { canonical: "SIEMENS", aliases: ["SIEMENS", "SIEMENSAG"] },
  {
    canonical: "ALLENBRADLEY",
    aliases: ["ALLENBRADLEY", "ROCKWELL", "ROCKWELLAUTOMATION", "ROCKWELLAUTOMATIONINC", "AB", "A-B"],
  },
  {
    canonical: "SCHNEIDERELECTRIC",
    aliases: ["SCHNEIDER", "SCHNEIDERELECTRIC", "TELEMECANIQUE", "MODICON", "SQUARE D", "SQUARED"],
  },
  { canonical: "MITSUBISHIELECTRIC", aliases: ["MITSUBISHI", "MITSUBISHIELECTRIC"] },
  { canonical: "OMRON", aliases: ["OMRON", "OMRONINDUSTRIALAUTOMATION"] },
  { canonical: "PHOENIXCONTACT", aliases: ["PHOENIXCONTACT"] },
  { canonical: "BOSCHREXROTH", aliases: ["BOSCHREXROTH", "REXROTH"] },
  { canonical: "WEIDMULLER", aliases: ["WEIDMULLER", "WEIDMULLERINTERFACE", "WEIDMÜLLER"] },
  { canonical: "BANDR", aliases: ["B&R", "B AND R", "BRAUTOMATION", "BERNECKERANDRAINER"] },
  { canonical: "PEPPERLFUCHS", aliases: ["PEPPERL+FUCHS", "PEPPERLFUCHS", "P+F"] },
  { canonical: "ENDRESSHAUSER", aliases: ["ENDRESS+HAUSER", "ENDRESSHAUSER", "E+H"] },
  { canonical: "EMERSON", aliases: ["EMERSON", "GEFANUC", "GEAUTOMATION"] },
  { canonical: "ABB", aliases: ["ABB"] },
  { canonical: "WAGO", aliases: ["WAGO"] },
  { canonical: "BECKHOFF", aliases: ["BECKHOFF"] },
  { canonical: "FESTO", aliases: ["FESTO"] },
  { canonical: "PILZ", aliases: ["PILZ"] },
  { canonical: "SICK", aliases: ["SICK"] },
  { canonical: "KEYENCE", aliases: ["KEYENCE"] },
  { canonical: "YASKAWA", aliases: ["YASKAWA"] },
  { canonical: "DANFOSS", aliases: ["DANFOSS"] },
  {
    canonical: "EATON",
    aliases: ["EATON", "MOELLER", "CROUSEHINDS", "CROUSE-HINDS", "CROUSEHINDSSERIES", "MTL"],
  },
];

const MANUFACTURER_ALIAS_MAP = new Map(
  MANUFACTURER_IDENTITIES.flatMap(({ canonical, aliases }) => aliases.map((alias) => [normalizeMpn(alias), canonical])),
);

/** Mirrors the database's normalized_manufacturer representation. */
export function normalizeManufacturer(value: string | null | undefined): string {
  return normalizeMpn(value);
}

/** Resolves well-known trading names for manufacturer agreement checks. */
export function canonicalManufacturerIdentity(value: string | null | undefined): string {
  const normalized = normalizeManufacturer(value);
  return MANUFACTURER_ALIAS_MAP.get(normalized) ?? extractManufacturerIdentity(value) ?? normalized;
}

/**
 * One conservative catalogue lookup fallback for Eaton Crouse-Hinds MTL
 * searches. A trailing digit after a letter is kept as the requested identity;
 * the returned base is only suitable for a separately labelled variant search.
 */
export function getManufacturerPartLookupFallback(
  value: string | null | undefined,
  manufacturer?: string | null,
): string | null {
  const mpn = normalizeMpn(value);
  const identity = canonicalManufacturerIdentity(manufacturer || inferManufacturerFromMpn(mpn));
  return identity === "EATON" && /^MTL\d{4}[A-Z]\d$/.test(mpn)
    ? mpn.slice(0, -1)
    : null;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function flexibleMpnPattern(normalizedMpn: string): RegExp {
  const characters = [...normalizedMpn].map(escapeRegExp).join("[^A-Z0-9]*");
  return new RegExp(`(^|[^A-Z0-9])(${characters})(?=$|[^A-Z0-9])`, "i");
}

/** A product range or electrical specification does not identify one orderable part. */
export function isDescriptivePartSearchToken(value: string): boolean {
  const normalized = normalizeMpn(value);
  const namedRanges = new Set([
    "ET200", "ET200SP", "ET200M", "S71200", "S71500", "S7300", "S7400",
    "SLC500", "PLC5", "MICRO800", "GOT2000", "AC500", "CP600",
  ]);
  if (namedRanges.has(normalized)
    || /^(?:CONTROLLOGIX|COMPACTLOGIX|MICROLOGIX|POWERFLEX|PANELVIEW(?:PLUS)?)(?:\d{1,4})?$/.test(normalized)
    || /^6(?:ES|AV|GK|EP|SL|FX|BK)\d{1,4}$/.test(normalized)
    || /^CPU1[25]\d{2}[CF]?$/.test(normalized)) return true;

  // Anchor the complete token so a catalogue number containing a specification
  // (for example PSU-24V-10A or EL-16CH-24DI) keeps its identity.
  return /^(?:\d+(?:V(?:DC|AC)?|MA|A|KW|W|HP|HZ|CHANNELS?|CH|POINTS?|PORTS?|POLES?|BITS?|GB|MB|GBP|EUR|DI|DO|AI|AO))+$/.test(normalized)
    || /^(?:IP\d{2}K?)+$/.test(normalized)
    || /^[13]AC\d+(?:V|VAC)?$/.test(normalized)
    || /^(?:(?:DI|DO|AI|AO)\d+X?|\d+X)\d+V(?:DC|AC)?$/.test(normalized);
}

/** Normalize presentation only. Never substitute characters or discard revisions. */
export function parsePartSearchInput(input: string): { partNumber: string | null; manufacturer: string | null } {
  let text = input.replace(/[\u2010-\u2015\u2212]/g, '-').replace(/\s+/g, ' ').trim();
  let manufacturer: string | null = null;
  const aliases = MANUFACTURER_IDENTITIES.flatMap(entry => entry.aliases).sort((a,b) => b.length - a.length);
  for (const alias of aliases) {
    const normalizedAlias = normalizeMpn(alias);
    const pattern = [...normalizedAlias].map(escapeRegExp).join('[\\s&.+-]*')
      + (normalizedAlias.length <= 3 ? "\\.?" : "");
    const prefix = new RegExp(`^(${pattern})(?:\\s*:\\s*|\\s+)(?=[A-Z0-9])`, 'i').exec(text);
    const suffix = new RegExp(`\\s+(${pattern})$`, 'i').exec(text);
    if (prefix) { manufacturer = prefix[1]; text = text.slice(prefix[0].length); break; }
    if (suffix) { manufacturer = suffix[1]; text = text.slice(0, suffix.index); break; }
  }
  text = text.replace(/\s*([/_.+-])\s*/g, '$1').trim();
  const standalone = /^[A-Z0-9][A-Z0-9._/+:-]*$/i.test(text);
  const spacedSegments = text.split(/\s+/);
  const siemensSpacedFormat = /^6(?:ES|AV|GK|EP|SL|FX|BK)\s*\d[\dA-Z]*(?:[ -]+[A-Z0-9]+(?:[._/+:-][A-Z0-9]+)*){1,5}$/i.test(text)
    && spacedSegments.slice(1).every((segment, index) => /\d/.test(segment)
      && !isDescriptivePartSearchToken(segment)
      && !inferManufacturerFromMpn(segment)
      // Long continuations belong directly after the short Siemens prefix.
      // Later groups are short suffixes, not a second pasted catalogue number.
      && (normalizeMpn(segment).length < 7
        || (index === 0 && normalizeMpn(spacedSegments[0]).length < 8 && /^\d/.test(segment))));
  const spacedFormat = siemensSpacedFormat
    || /^(?:1756|1769|1768|1746|1734|1771|1783|1794|2711)\s+[A-Z][A-Z0-9]*(?:[/.-][A-Z0-9]+)*$/i.test(text);
  const normalized = normalizeMpn(text);
  const exact = !isDescriptivePartSearchToken(text) && (standalone || spacedFormat) && (/^\d{7,}$/.test(normalized)
    || (normalized.length >= 7 && /\d/.test(normalized) && /[A-Z]/.test(normalized)));
  return { partNumber: exact ? text : null, manufacturer };
}

/** Suggestions require an explicit user choice and never enter exact matching. */
export function possiblePartQueryCorrections(input: string): string[] {
  const parsed = parsePartSearchInput(input);
  if (!parsed.partNumber || !/^6(?:ES|AV|GK|EP|SL|FX|BK)\s*\d/i.test(parsed.partNumber)) return [];
  const candidate = parsed.partNumber.replace(/([0-9])O(?=[-/]|$)/gi, (_match, digit: string) => `${digit}0`)
    .replace(/-O(?=[A-Z]{2}[0-9]$)/i, '-0');
  return candidate === parsed.partNumber ? [] : [[parsed.manufacturer, candidate].filter(Boolean).join(' ')];
}

function candidateScore(value: string): number {
  const normalized = normalizeMpn(value);
  const separators = (value.match(/[-_./+]/g) ?? []).length;
  return normalized.length * 10 + separators;
}

/**
 * Extracts the likely catalogue number from a supplier title. When an expected
 * MPN is supplied, the exact formatted substring wins and packaging/title text
 * is excluded from the identity comparison.
 */
export function extractMpnFromSupplierTitle(
  title: string | null | undefined,
  expectedMpn?: string | null,
): string | null {
  const text = (title ?? "").replace(/\s+/g, " ").trim();
  if (!text) return null;

  // A standalone supplier part-number field is authoritative. Keep suffixes
  // such as /B or -10 so they can be classified as revisions/packaging
  // variants; only prose titles have their surrounding qualifiers removed.
  if (!/\s/.test(text)) {
    const standalone = text.match(/^[A-Z0-9][A-Z0-9._/+:-]{4,}$/i)?.[0];
    const normalizedStandalone = normalizeMpn(standalone);
    if (standalone && ((/\d/.test(normalizedStandalone) && /[A-Z]/.test(normalizedStandalone)) || /^\d{7,}$/.test(normalizedStandalone))) {
      return standalone;
    }
  }

  const expected = normalizeMpn(expectedMpn);
  if (expected.length >= 5) {
    const expectedMatch = flexibleMpnPattern(expected).exec(text);
    if (expectedMatch?.[2]) {
      const tail = text.slice(expectedMatch.index + expectedMatch[0].length);
      const suffix = tail.match(/^(?:[/_+.-][A-Z0-9]+)+/i)?.[0]
        ?? tail.match(/^\s+(?:rev(?:ision)?\.?|series)\s+[A-Z0-9]+\b/i)?.[0]
        ?? '';
      return `${expectedMatch[2]}${suffix}`.trim();
    }
  }

  const candidates = new Set<string>();
  // Siemens catalogue numbers commonly contain a significant space after the
  // first four characters (for example 6ES7 214-1AG40-0XB0).
  for (const match of text.matchAll(/\b6(?:ES|AV|GK|EP|SL|FX)\s*\d[A-Z0-9]*(?:[\s-]+[A-Z0-9]*\d[A-Z0-9]*){1,5}\b/gi)) {
    candidates.add(match[0].trim());
  }
  // Most PLC MPNs are a single punctuation-delimited token. Require a digit so
  // ordinary uppercase words in listing titles cannot become part identities.
  for (const match of text.matchAll(/(?:^|[^A-Z0-9])([A-Z0-9][A-Z0-9._/+:-]{4,})(?=$|[^A-Z0-9])/gi)) {
    const value = match[1];
    const normalized = normalizeMpn(value);
    if ((/\d/.test(normalized) && /[A-Z]/.test(normalized) && normalized.length >= 6)
      || /^\d{7,}$/.test(normalized)) {
      candidates.add(value);
    }
  }

  return [...candidates].sort((left, right) => candidateScore(right) - candidateScore(left))[0] ?? null;
}

/** Returns a known canonical manufacturer mentioned in free text. */
export function extractManufacturerIdentity(value: string | null | undefined): string | null {
  const text = value ?? "";
  const normalizedText = normalizeMpn(text);
  for (const { canonical, aliases } of MANUFACTURER_IDENTITIES) {
    for (const alias of [...aliases].sort((left, right) => right.length - left.length)) {
      const normalizedAlias = normalizeMpn(alias);
      if (normalizedAlias.length > 3 && normalizedText.includes(normalizedAlias)) return canonical;
      const separatedAlias = [...normalizedAlias].map(escapeRegExp).join("[\\s&.+-]*");
      // Short aliases must be separate words, not a segment in a catalogue
      // number such as AB-12345. A sentence-ending dot or label colon is fine.
      if (normalizedAlias && new RegExp(`(^|[^A-Z0-9._/+:-])${separatedAlias}(?=$|[^A-Z0-9._/+:-]|[.:](?:\\s|$))`, "i").test(text)) return canonical;
    }
  }
  return null;
}

function commonPrefixLength(a: string, b: string): number {
  const limit = Math.min(a.length, b.length);
  let index = 0;
  while (index < limit && a[index] === b[index]) index += 1;
  return index;
}

function isOnlyZeroOhAmbiguity(a: string, b: string): boolean {
  if (a.length !== b.length || a === b) return false;
  let sawAmbiguity = false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] === b[index]) continue;
    if ((a[index] === "0" && b[index] === "O") || (a[index] === "O" && b[index] === "0")) {
      sawAmbiguity = true;
      continue;
    }
    return false;
  }
  return sawAmbiguity;
}

/**
 * Classifies MPN identity only. Compatibility and successor claims are
 * explicitly outside this classifier's remit.
 */
export function classifyMpnMatch(
  query: string,
  candidateMpn: string | null | undefined,
): OfferMatchType {
  const extractedQuery = extractMpnFromSupplierTitle(query);
  const requestedMpn = extractedQuery ?? query;
  const extractedCandidate = extractMpnFromSupplierTitle(candidateMpn, requestedMpn);
  const candidate = extractedCandidate ?? candidateMpn ?? "";
  const normalizedQuery = normalizeMpn(requestedMpn);
  const normalizedCandidate = normalizeMpn(candidate);

  if (!normalizedCandidate || !normalizedQuery) return "fuzzy";
  if (normalizedQuery === normalizedCandidate) {
    return "exact";
  }

  // A 0/O substitution is never normalized away or promoted to a variant. It
  // remains a possible match for a human to review.
  if (isOnlyZeroOhAmbiguity(normalizedQuery, normalizedCandidate)) return "fuzzy";

  const shorterLength = Math.min(normalizedQuery.length, normalizedCandidate.length);
  const prefixLength = commonPrefixLength(normalizedQuery, normalizedCandidate);
  const isPrefix = normalizedQuery.startsWith(normalizedCandidate) || normalizedCandidate.startsWith(normalizedQuery);
  const looksLikeRevision = shorterLength >= 6 && (
    isPrefix || prefixLength / shorterLength >= 0.72
  );

  return looksLikeRevision ? "variant" : "fuzzy";
}

/**
 * Applies the manufacturer agreement rule on top of MPN classification.
 * Missing manufacturer context in the request is neutral; when the user or
 * canonical part supplies one, a missing or conflicting candidate cannot be
 * labelled exact.
 */
export function classifyOfferIdentity(input: OfferIdentityInput): OfferIdentityMatch {
  const requestedMpn = extractMpnFromSupplierTitle(input.requestedMpn) ?? input.requestedMpn.trim();
  const candidateText = input.candidateTitle || input.candidateMpn || "";
  const explicitCandidate = input.candidateMpn?.trim();
  const authoritativeMpn = explicitCandidate && /^[A-Z0-9][A-Z0-9._/+:-]*$/i.test(explicitCandidate) && /\d/.test(explicitCandidate)
    ? explicitCandidate : null;
  const candidateMpn = authoritativeMpn ?? extractMpnFromSupplierTitle(candidateText, requestedMpn)
    ?? extractMpnFromSupplierTitle(input.candidateMpn)
    ?? (input.candidateMpn ?? "").trim();
  const requestedManufacturer = canonicalManufacturerIdentity(
    input.requestedManufacturer || extractManufacturerIdentity(input.requestedMpn),
  );
  const statedCandidateManufacturer = input.candidateManufacturer || extractManufacturerIdentity(candidateText);
  const groundedUnknownManufacturer = input.requestedManufacturer
    && normalizeMpn(candidateText).includes(normalizeMpn(input.requestedManufacturer))
      ? input.requestedManufacturer
      : null;
  const candidateManufacturer = canonicalManufacturerIdentity(
    statedCandidateManufacturer || groundedUnknownManufacturer,
  );
  const manufacturerMatch = requestedManufacturer
    ? Boolean(candidateManufacturer && candidateManufacturer === requestedManufacturer)
    : null;
  const mpnMatch = classifyMpnMatch(requestedMpn, candidateMpn);
  const matchType = mpnMatch === "exact" && manufacturerMatch === false
    ? "fuzzy"
    : mpnMatch === "variant" && manufacturerMatch === false
      ? "fuzzy"
      : mpnMatch;
  const explanation = matchType === "exact"
    ? manufacturerMatch === true ? "Normalized MPN and manufacturer agree" : "Normalized MPN agrees; no conflicting manufacturer was requested"
    : mpnMatch === "exact" && manufacturerMatch === false
      ? "MPN agrees but manufacturer identity is missing or conflicts"
      : matchType === "variant"
        ? "Catalogue number is a related revision or suffix"
        : "Identity is not confirmed";

  return {
    matchType,
    requestedMpn,
    candidateMpn,
    normalizedRequestedMpn: normalizeMpn(requestedMpn),
    normalizedCandidateMpn: normalizeMpn(candidateMpn),
    normalizedRequestedManufacturer: requestedManufacturer,
    normalizedCandidateManufacturer: candidateManufacturer,
    manufacturerMatch,
    explanation,
  };
}

export function canonicalIdentityKey(manufacturer: string, mpn: string): string | null {
  const normalizedManufacturer = normalizeManufacturer(manufacturer);
  const normalizedMpn = normalizeMpn(mpn);
  return normalizedManufacturer && normalizedMpn
    ? `${normalizedManufacturer}:${normalizedMpn}`
    : null;
}
